import React from "react";
import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, X } from "lucide-react";
import { formatPercent, formatUsd } from "../utils/upDownFormatters";

export function EarlyCloseModal({
  isOpen,
  isClosing,
  cancelOrder,
  lastCancelOrderRef,
  closeTradePreview,
  lastClosePreviewRef,
  onClose,
  onConfirm,
}) {
  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const activeOrder = cancelOrder || lastCancelOrderRef?.current;
  const activePreview = closeTradePreview || lastClosePreviewRef?.current;
  if (!activeOrder || !activePreview) return null;

  return createPortal(
    <div
      className={`early-close-modal-root ${isClosing ? "is-closing" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-trade-early-title"
    >
      <div
        className="early-close-modal-backdrop"
        onClick={onClose}
      />

      <div
        className={`early-close-modal-panel ${isClosing ? "is-closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="early-close-dismiss-btn"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Warning Icon Badge */}
        <div
          className={`early-close-badge-icon ${activePreview.isWinning ? "is-winning" : "is-losing"}`}
        >
          <AlertTriangle size={24} strokeWidth={2.4} />
        </div>

        {/* Header Titles */}
        <h3
          id="close-trade-early-title"
          className="early-close-title"
        >
          Close Trade Early?
        </h3>
        <p className="early-close-subtitle">
          Settle your active position at the live market price
        </p>

        {/* Active Trade Summary Strip */}
        <div className="early-close-trade-strip">
          <div className="early-close-trade-side">
            <span
              className={`early-close-side-pill ${activeOrder.side === "up" ? "is-up" : "is-down"}`}
            >
              {activeOrder.side === "up" ? "▲ UP" : "▼ DOWN"}
            </span>
            <span className="early-close-amount-tag">
              ${Number(activeOrder.amount).toFixed(2)}
            </span>
          </div>

          <div className="early-close-price-track">
            <span>Entry: {Number(activeOrder.entryPrice) || "--"}</span>
            <span className="early-close-track-arrow">→</span>
            <span
              className={
                activePreview.isWinning
                  ? "text-[#00c076] font-bold"
                  : "text-[#f6465d] font-bold"
              }
            >
              Now: {Number(activePreview.currentPrice) || "--"}
            </span>
          </div>
        </div>

        {/* Highlight Return Card */}
        <div
          className={`early-close-pnl-card ${activePreview.isWinning ? "is-winning" : "is-losing"}`}
        >
          <span className="early-close-pnl-label">
            {activePreview.isWinning
              ? "Profit Return Rate"
              : "Loss Return Rate"}
          </span>

          <div
            className={`early-close-pnl-percent ${activePreview.isWinning ? "is-winning" : "is-losing"}`}
          >
            {activePreview.displayPercent >= 0 ? "+" : ""}
            {formatPercent(activePreview.displayPercent)}
          </div>

          <div
            className={`early-close-pnl-usd ${activePreview.isWinning ? "is-winning" : "is-losing"}`}
          >
            P&amp;L: {activePreview.isWinning ? "+" : "-"}
            {formatUsd(Math.abs(activePreview.closeProfit))}
          </div>

          <div className="early-close-pnl-divider" />

          <div className="early-close-payout-row">
            <span className="early-close-payout-label">
              Returned to Balance
            </span>
            <span className="early-close-payout-val">
              {formatUsd(activePreview.closePayout)}
            </span>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="early-close-warning-box">
          <AlertCircle size={14} className="early-close-warn-icon" />
          <span>This action settles immediately and cannot be undone</span>
        </div>

        {/* Action Buttons */}
        <div className="early-close-actions-grid">
          <button
            type="button"
            className="early-close-btn-cancel"
            onClick={onClose}
          >
            Keep Open
          </button>
          <button
            type="button"
            className={`early-close-btn-confirm ${activePreview.isWinning ? "is-winning" : "is-losing"}`}
            onClick={onConfirm}
          >
            Yes, Close Trade
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

